import React, { useEffect, useState, useRef, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ComingSoonModal from '../../pages/ComingSoon/ComingSoonModal';
import { isSuperAdmin, isPropertyOnboardingStaff, isFM } from '../../../utils/roleUtils';
import { getApiEndpoint } from '../../../config/api';
import UserRoleBadge from '../UserRoleBadge';
import '../shared.css';
import './MainHeader.css';

const logoUrl = process.env.PUBLIC_URL
  ? `${process.env.PUBLIC_URL}/cloudsafe-logo.png`
  : '/cloudsafe-logo.png';

const UserAvatar = () => (
  <div className="user-avatar">
    <i className="fas fa-user"></i>
  </div>
);

// Icon map for notification types
const NOTIF_ICONS = {
  job_invite: 'fa-briefcase',
  job_assigned: 'fa-briefcase',
  quote_received: 'fa-file-invoice-dollar',
  quote_approved: 'fa-check-circle',
  quote_declined: 'fa-exclamation-triangle',
  job_started: 'fa-play',
  job_ended: 'fa-stop',
  conflict: 'fa-exclamation-triangle',
};

const NOTIF_COLORS = {
  job_invite: 'mh-ni-blue',
  job_assigned: 'mh-ni-blue',
  quote_received: 'mh-ni-amber',
  quote_approved: 'mh-ni-green',
  quote_declined: 'mh-ni-red',
  job_started: 'mh-ni-green',
  job_ended: 'mh-ni-gray',
  conflict: 'mh-ni-red',
};

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function MainHeader() {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [effectiveRoles, setEffectiveRoles] = useState([]);
  const mobileMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      fetch(getApiEndpoint('/api/users/me'), { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(me => {
          if (!me?.id) return;
          return fetch(getApiEndpoint(`/api/rbac/users/${me.id}/roles`), { headers: { Authorization: `Bearer ${token}` } });
        })
        .then(r => (r && r.ok ? r.json() : []))
        .then(roles => {
          const names = Array.isArray(roles) ? roles.map(r => r.role_name || r.name).filter(Boolean) : [];
          setEffectiveRoles(names);
        })
        .catch(() => {});
    } catch (_) {}
  }, []);

  // Fetch unread notification count (lightweight poll)
  const fetchUnreadCount = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(getApiEndpoint('/api/notifications?limit=1'), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setUnreadCount(data.unreadCount || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch recent notifications when dropdown opens
  const fetchNotifications = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setNotifLoading(true);
    fetch(getApiEndpoint('/api/notifications?limit=8'), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      })
      .catch(() => {})
      .finally(() => setNotifLoading(false));
  }, []);

  const toggleNotifDropdown = () => {
    const next = !showNotifDropdown;
    setShowNotifDropdown(next);
    if (next) fetchNotifications();
  };

  const markRead = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await fetch(getApiEndpoint(`/api/notifications/${id}/read`), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await fetch(getApiEndpoint('/api/notifications/read-all'), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) {}
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowUserMenu(false);
    setShowNotifDropdown(false);
  }, [location.pathname]);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handler = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileMenuOpen]);

  // Close user dropdown on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  // Close notification dropdown on outside click
  useEffect(() => {
    if (!showNotifDropdown) return;
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifDropdown]);

  const propertyMatch = location.pathname.match(/\/properties\/(\d+)/);
  const propertyDashboardMatch = location.pathname.match(/\/propertydashboard\/(\d+)/);
  const propertyId = propertyMatch ? propertyMatch[1] : (propertyDashboardMatch ? propertyDashboardMatch[1] : null);

  const documentsLink = propertyId
    ? (propertyDashboardMatch ? `/propertydashboard/${propertyId}/documents` : `/properties/${propertyId}/documents`)
    : null;

  const inspectionsLink = propertyId
    ? (propertyDashboardMatch ? `/propertydashboard/${propertyId}/inspections` : `/properties/${propertyId}/inspections`)
    : null;

  const logsLink = propertyId
    ? (propertyDashboardMatch ? `/propertydashboard/${propertyId}/logs` : `/properties/${propertyId}/logs`)
    : null;

  const settingsLink = propertyId
    ? (propertyDashboardMatch ? `/propertydashboard/${propertyId}/settings` : `/properties/${propertyId}/settings`)
    : '/settings';

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const onMainDashboard = location.pathname === '/dashboard';
  const hasEffectiveAdmin = effectiveRoles.includes('super_admin') || effectiveRoles.includes('property-onboarding-staff');
  const canSeeAdmin = (isSuperAdmin() || isPropertyOnboardingStaff() || hasEffectiveAdmin) && onMainDashboard;
  const isContractor = effectiveRoles.includes('contractor') || localStorage.getItem('userRole') === 'CONTRACTOR';

  return (
    <header className="dashboard-header" ref={mobileMenuRef}>
      <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
        <img src={logoUrl} alt="CloudSafe Logo" className="logo" />
      </Link>

      <div className="header-right">
        {/* Desktop nav */}
        <nav className="nav-links">
          <Link to="/dashboard" className="nav-link">Dashboard</Link>
          {isFM() && (
            <Link to="/fm/dashboard" className="nav-link">Overview</Link>
          )}
          <Link to="/reports" className="nav-link">Reports</Link>
          {isSuperAdmin() && (
            <Link to="/showcase" className="nav-link mh-showcase-link">Showcase</Link>
          )}
          {canSeeAdmin && (
            <Link to="/dashboard/admin-panel" className="nav-link">Admin</Link>
          )}
          {propertyId && (
            <>
              {documentsLink && <Link to={documentsLink} className="nav-link">Documents</Link>}
              {inspectionsLink && <Link to={inspectionsLink} className="nav-link">Inspections</Link>}
              {logsLink && <Link to={logsLink} className="nav-link">Logs</Link>}
            </>
          )}
        </nav>

        <div className="header-icons">
          <UserRoleBadge className="mh-role-badge" />
          {propertyId && (
            <div className="icon" onClick={() => window.dispatchEvent(new CustomEvent('openPropertySearch'))}><i className="fas fa-search"></i></div>
          )}

          {/* Notification bell with dropdown */}
          <div className="mh-notif-wrap" ref={notifRef}>
            <div className="icon mh-bell" onClick={toggleNotifDropdown}>
              <i className="fas fa-bell"></i>
              {unreadCount > 0 && (
                <span className="mh-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </div>

            {showNotifDropdown && (
              <div className="mh-notif-dropdown">
                <div className="mh-nd-header">
                  <span className="mh-nd-title">Notifications</span>
                  {unreadCount > 0 && (
                    <button className="mh-nd-mark-all" onClick={markAllRead} type="button">
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="mh-nd-list">
                  {notifLoading ? (
                    <div className="mh-nd-loading">
                      {[1,2,3].map(i => (
                        <div key={i} className="mh-nd-skel">
                          <div className="mh-nd-skel-icon" />
                          <div className="mh-nd-skel-body">
                            <div className="mh-nd-skel-line mh-nd-skel-w60" />
                            <div className="mh-nd-skel-line mh-nd-skel-w80" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="mh-nd-empty">
                      <i className="fas fa-bell-slash"></i>
                      <span>No notifications</span>
                    </div>
                  ) : (
                    notifications.map(n => {
                      const iconClass = NOTIF_ICONS[n.type] || 'fa-bell';
                      const colorClass = NOTIF_COLORS[n.type] || 'mh-ni-gray';
                      return (
                        <button
                          key={n.id}
                          className={`mh-nd-item ${!n.is_read ? 'mh-nd-unread' : ''}`}
                          onClick={() => {
                            if (!n.is_read) markRead(n.id);
                          }}
                          type="button"
                        >
                          {!n.is_read && <span className="mh-nd-unread-bar" />}
                          <span className={`mh-nd-icon ${colorClass}`}>
                            <i className={`fas ${iconClass}`}></i>
                          </span>
                          <span className="mh-nd-body">
                            <span className="mh-nd-msg">{n.title}</span>
                            <span className="mh-nd-sub">{n.message}</span>
                            <span className="mh-nd-time">{timeAgo(n.created_at)}</span>
                          </span>
                          {!n.is_read && <span className="mh-nd-dot" />}
                        </button>
                      );
                    })
                  )}
                </div>

                <button
                  className="mh-nd-footer"
                  onClick={() => { setShowNotifDropdown(false); navigate('/notifications'); }}
                  type="button"
                >
                  View All Notifications
                </button>
              </div>
            )}
          </div>

          <div className="icon" onClick={() => navigate('/support/tickets')}><i className="fas fa-headset"></i></div>
          <div className="user-avatar-container" ref={userMenuRef} aria-haspopup="menu" aria-expanded={showUserMenu} {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
            <UserAvatar />
            {showUserMenu && (
              <div className="user-menu">
                <Link to={settingsLink} onClick={() => setShowUserMenu(false)}>Settings</Link>
                <Link to="/account-settings" onClick={() => setShowUserMenu(false)}>Account</Link>
                <button onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="mobile-hamburger"
            onClick={() => setMobileMenuOpen(prev => !prev)}
            aria-label="Toggle navigation menu"
          >
            <span className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
              <span /><span /><span />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      <div className={`mobile-nav-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <nav className="mobile-nav-links">
          <Link to="/dashboard" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <i className="fas fa-th-large" /> Dashboard
          </Link>
          {isFM() && (
            <Link to="/fm/dashboard" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <i className="fas fa-th-list" /> Overview
            </Link>
          )}
          <Link to="/reports" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <i className="fas fa-chart-bar" /> Reports
          </Link>
          {isSuperAdmin() && (
            <Link to="/showcase" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <i className="fas fa-wand-magic-sparkles" /> Showcase
            </Link>
          )}
          {canSeeAdmin && (
            <Link to="/dashboard/admin-panel" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
              <i className="fas fa-shield-alt" /> Admin Panel
            </Link>
          )}
          {propertyId && (
            <>
              {documentsLink && (
                <Link to={documentsLink} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                  <i className="fas fa-folder" /> Documents
                </Link>
              )}
              {inspectionsLink && (
                <Link to={inspectionsLink} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                  <i className="fas fa-clipboard-check" /> Inspections
                </Link>
              )}
              {logsLink && (
                <Link to={logsLink} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                  <i className="fas fa-history" /> Activity Logs
                </Link>
              )}
            </>
          )}
          <div className="mobile-nav-divider" />
          <Link to="/support/tickets" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <i className="fas fa-headset" /> Support
          </Link>
          <Link to={settingsLink} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <i className="fas fa-cog" /> Settings
          </Link>
          <Link to="/account-settings" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
            <i className="fas fa-user-cog" /> Account Settings
          </Link>
          <button className="mobile-nav-link mobile-nav-logout" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt" /> Logout
          </button>
        </nav>
      </div>

      {/* Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      <ComingSoonModal
        isOpen={isComingSoonOpen}
        onClose={() => setIsComingSoonOpen(false)}
        featureName="This feature"
      />
    </header>
  );
}
